/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { PageLayout } from '../common/PageLayout';
import { WidgetWrapper } from '../common/widgets/WidgetWrapper';
import { ISession } from '../../../app/resource/session/session.types';
import { meetingArchiveSelectors } from '../../../app/resource/meeting-archives/meeting-archives.selectors';
import { authSelectors } from '../../../app/auth/auth.selectors';
import { userSelectors } from '../../../app/resource/user/user.selectors';
import { convertAndFormatDateToUserTimeZone } from '../../../utils/date.utils';
import { initialArchivePage } from '../../../app/page/page-storage/meeting-archive/meeting-archive.actions';
import { IMeetingArchive } from '../../../app/resource/meeting-archives/meeting-archives.types';
import { Goal } from '../../../app/resource/score/score.types';
import moment from 'moment';
import { formatScoreDisplayValue } from '../../../utils/scoreFormat.utils';

const goalIcon = (goal: Goal): string => (goal === '>=' ? '≥' : goal === '<=' ? '≤' : goal === '==' ? '=' : goal);

export const ArchivePage = () => {
  const dispatch = useDispatch();
  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'meetingArchives');
  const { sessionId } = useParams<{ sessionId: ISession['id'] }>();

  useEffect(() => {
    dispatch(initialArchivePage(sessionId));
  }, [dispatch, sessionId]);

  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const user = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const archive = useSelectWithParams(meetingArchiveSelectors.selectResourceById, sessionId) as IMeetingArchive;

  const date =
    archive && user && convertAndFormatDateToUserTimeZone(archive.meetingDate, user.timezone, 'MMMM Do, YYYY');

  return (
    <PageLayout showLoader={showLoader} smallSidebar>
      {showLoader ? (
        <>
          <span className="css-loader--meeting-dashboard-navbar"></span>
        </>
      ) : (
        date &&
        user &&
        archive.attendeesMembers && (
          <>
            <WidgetWrapper name={date}>
              <div className="archive-content">
                <table className="body-wrap">
                  <tr>
                    <td className="container">
                      <table>
                        <tr>
                          <td className="content">
                            <div className="meeting-title">
                              <div className="column">
                                <h4>Meeting: {archive.meetingTitle}</h4>
                                <p style={{ color: '#5dbb63' }}>
                                  Meeting Date:{' '}
                                  {convertAndFormatDateToUserTimeZone(archive.meetingDate, user.timezone, 'YYYY-MM-DD')}
                                  <br />
                                  Meeting Time:{' '}
                                  {convertAndFormatDateToUserTimeZone(archive.meetingDate, user.timezone, 'HH:mm:ss')}
                                  <br />
                                  Meeting Duration: {archive.meetingDuration} min
                                </p>
                              </div>
                            </div>
                            <p className="meeting-desc"></p>
                            <h2>
                              Meeting Attendance {archive.attendeesMembers.length + archive.attendeesAdmins.length}/
                              {archive.attendeesMembers.length +
                                archive.attendeesAdmins.length +
                                archive.notAttended.length}
                            </h2>
                            <table width="100%" style={{ fontSize: '12px' }} className="small-table">
                              <tbody>
                                {archive.attendeesAdmins.map((attendee) => (
                                  <tr key={attendee.id}>
                                    <td align="left">
                                      <span style={{ color: 'green' }}> ● </span>
                                      <strong>
                                        {' '}
                                        {attendee.firstName} {attendee.lastName}
                                      </strong>{' '}
                                      / {attendee.jobTitle}
                                    </td>
                                  </tr>
                                ))}
                                {archive.attendeesMembers.map((attendee) => (
                                  <tr key={attendee.id}>
                                    <td align="left">
                                      <span style={{ color: 'green' }}> ● </span>
                                      <strong>
                                        {' '}
                                        {attendee.firstName} {attendee.lastName}
                                      </strong>{' '}
                                      / {attendee.jobTitle}
                                    </td>
                                  </tr>
                                ))}
                                {archive.notAttended.map((attendee) => (
                                  <tr key={attendee.id}>
                                    <td align="left" key={attendee.id}>
                                      <span style={{ color: 'red' }}> ● </span>
                                      <strong>
                                        {' '}
                                        {attendee.firstName} {attendee.lastName}
                                      </strong>{' '}
                                      / {attendee.jobTitle}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <h2>KPIs</h2>
                            <table width="100%" style={{ fontSize: '12px' }} className="small-table">
                              <tbody>
                                {archive.kpis.map((kpi) => (
                                  <>
                                    <tr>
                                      <td align="left">
                                        <strong>{kpi.fullName}</strong>
                                      </td>
                                    </tr>
                                    {kpi.kpis.map((kpiItem) => {
                                      const measuredEmpty = kpiItem.measured == null || kpiItem.measured === '';
                                      const isOnTrack =
                                        (kpiItem.measured == kpiItem.score && kpiItem.goal === '==') ||
                                        (kpiItem.measured >= kpiItem.score && kpiItem.goal === '>=') ||
                                        (kpiItem.measured <= kpiItem.score && kpiItem.goal === '<=');
                                      const measuredDisplay = formatScoreDisplayValue(
                                        kpiItem.measured,
                                        kpiItem.type,
                                        kpiItem.units,
                                      );
                                      const scoreDisplay = formatScoreDisplayValue(
                                        kpiItem.score,
                                        kpiItem.type,
                                        kpiItem.units,
                                      );

                                      return (
                                        <tr key={kpiItem.id}>
                                          <td align="left">
                                            {kpiItem.title}:{' '}
                                            {measuredEmpty ? (
                                              <>
                                                <span
                                                  className="open-todo"
                                                  style={{ backgroundColor: '#dcdcdc', color: '#333333' }}
                                                >
                                                  Not Set
                                                </span>{' '}
                                                {goalIcon(kpiItem.goal)} {scoreDisplay}
                                              </>
                                            ) : (
                                              <>
                                                <span
                                                  className={isOnTrack ? 'open-todo' : 'overdue-todo'}
                                                  style={
                                                    isOnTrack ? { backgroundColor: '#ddf6f2', color: '#1bc3a7' } : {}
                                                  }
                                                >
                                                  {measuredDisplay}
                                                </span>{' '}
                                                {goalIcon(kpiItem.goal)} {scoreDisplay}
                                              </>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </>
                                ))}
                              </tbody>
                            </table>
                            <h2>Completed Action Items</h2>
                            <table
                              // border="0"
                              width="100%"
                              style={{ fontSize: '12px' }}
                              className="small-table"
                            >
                              <tbody>
                                {archive.completedActionItems.length === 0 && (
                                  <tr>
                                    <td align="center">No completed action items</td>
                                  </tr>
                                )}
                                {archive.completedActionItems.map((completedActionItem) => (
                                  <>
                                    <tr key={completedActionItem.firstName}>
                                      <td>
                                        <strong>
                                          {completedActionItem.firstName} {completedActionItem.lastName}
                                        </strong>
                                      </td>
                                    </tr>
                                    {completedActionItem.actionItems.map((actionItem) => (
                                      <tr key={actionItem.id}>
                                        <td align="left">
                                          {actionItem.title}
                                          {!actionItem.dueDate && (
                                            <span
                                              className="open-todo"
                                              style={{ backgroundColor: '#dcdcdc', color: '#333333' }}
                                            >
                                              Not Set
                                            </span>
                                          )}
                                          {actionItem.dueDate && new Date(actionItem.dueDate) > new Date() && (
                                            <span className="open-todo">
                                              {moment(actionItem.dueDate).format('YYYY-MM-DD')}
                                            </span>
                                          )}
                                          {actionItem.dueDate && new Date(actionItem.dueDate) < new Date() && (
                                            <span className="overdue-todo">
                                              {moment(actionItem.dueDate).format('YYYY-MM-DD')}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </>
                                ))}
                              </tbody>
                            </table>
                            <h2>Open Action Items</h2>
                            <table width="100%" style={{ fontSize: '12px' }} className="small-table">
                              <tbody>
                                {archive.openActionItems.length === 0 && (
                                  <tr>
                                    <td align="center">No completed action items</td>
                                  </tr>
                                )}
                                {archive.openActionItems.map((openActionItem) => (
                                  <>
                                    <tr key={openActionItem.firstName}>
                                      <td>
                                        <strong>
                                          {openActionItem.firstName} {openActionItem.lastName}
                                        </strong>
                                      </td>
                                    </tr>
                                    {openActionItem.actionItems.map((actionItem) => (
                                      <tr key={actionItem.id}>
                                        <td align="left">
                                          {actionItem.title}
                                          {!actionItem.dueDate && (
                                            <span
                                              className="open-todo"
                                              style={{ backgroundColor: '#dcdcdc', color: '#333333' }}
                                            >
                                              Not Set
                                            </span>
                                          )}
                                          {actionItem.dueDate && new Date(actionItem.dueDate) > new Date() && (
                                            <span className="open-todo">
                                              {moment(actionItem.dueDate).format('YYYY-MM-DD')}
                                            </span>
                                          )}
                                          {actionItem.dueDate && new Date(actionItem.dueDate) < new Date() && (
                                            <span className="overdue-todo">
                                              {moment(actionItem.dueDate).format('YYYY-MM-DD')}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </>
                                ))}
                              </tbody>
                            </table>
                            <h2>Meeting Scores</h2>
                            <table width="100%" style={{ fontSize: '12px' }} className="small-table">
                              <tbody>
                                {archive.scores.map((meetingScore) => (
                                  <tr key={meetingScore.userId}>
                                    <td align="left">
                                      <strong>
                                        {meetingScore.firstName} {meetingScore.lastName}:{' '}
                                      </strong>
                                      <span className="archive-score">{meetingScore.score}</span>
                                    </td>
                                  </tr>
                                ))}
                                {archive.notAttended.map((user) => (
                                  <tr key={user.id}>
                                    <td align="left">
                                      <span className="overdue-todo">Did not attend</span>
                                      <strong>
                                        {user.firstName} {user.lastName}
                                      </strong>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {archive.note && (
                              <p className="small-table-desc">
                                <strong>Comments:</strong>
                                <p style={{ fontSize: '12px' }}>{archive.note}</p>
                                <br />
                              </p>
                            )}
                            {archive.avgScore && (
                              <p className="archive-avg-score">
                                <span className="archive-score-big">{archive.avgScore}</span>
                                <strong>Average meeting score</strong>
                              </p>
                            )}
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </div>
            </WidgetWrapper>
          </>
        )
      )}
    </PageLayout>
  );
};
